/**
 * Week lessons data hook - manages week lessons fetching and lesson/subject selection
 *
 * Handles:
 * - Fetching lessons for a specific week
 * - Deriving selectedSubject and selectedLesson from URL
 * - Updating lesson status locally after start/complete
 */

import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { REQUEST_STATUS } from "@ts/enums/RequestStatus";
import { LESSON_STATUS } from "@ts/enums/CompletionStatus";
import { termsApi } from "@api/termsApi";
import { lessonsApi } from "@api/lessonsApi";
import type { Subject, Lesson, CsatTriggerPayload, CompleteLessonResponse } from "@ts/interfaces/terms/api/termsApiInterface";
import type { ErrorInterface } from "@ts/interfaces/ApiResponseInterface";

interface UseWeekLessonsDataParams {
  urlGradeCode: number | undefined;
  urlTermId: string | undefined;
  urlWeekId: string | undefined;
  lessonSlug: string | undefined;
  lessonId: string | undefined;
  invalidateTermsCache: () => void;
  invalidateSubjectsCache: () => void;
}

interface UseWeekLessonsDataReturn {
  currentWeekSubjects: Subject[];
  weekLessonsStatus: REQUEST_STATUS;
  weekLessonsError: ErrorInterface | null;
  selectedSubject: Subject | null;
  selectedLesson: Lesson | null;
  startLesson: (params: {
    gradeId: number;
    termId: number;
    weekId: number;
    lessonId: number;
    subjectSlug: string;
  }) => Promise<void>;
  completeLesson: (params: {
    gradeId: number;
    termId: number;
    weekId: number;
    lessonId: number;
    subjectSlug: string;
  }) => Promise<CsatTriggerPayload | null>;
  fetchSectionContent: <T>(params: {
    section: string;
    gradeId: number;
    termId: number;
    weekId: number;
    lessonId: number;
    subjectSlug: string;
  }) => Promise<T>;
  invalidateLessonsCache: () => void;
}

export function useWeekLessonsData({
  urlGradeCode,
  urlTermId,
  urlWeekId,
  lessonSlug,
  lessonId,
  invalidateTermsCache,
  invalidateSubjectsCache
}: UseWeekLessonsDataParams): UseWeekLessonsDataReturn {
  const [currentWeekSubjects, setCurrentWeekSubjects] = useState<Subject[]>([]);
  const [weekLessonsStatus, setWeekLessonsStatus] = useState<REQUEST_STATUS>(REQUEST_STATUS.IDLE);
  const [weekLessonsError, setWeekLessonsError] = useState<ErrorInterface | null>(null);

  const lastFetchedWeekKeyRef = useRef<string | undefined>(undefined);
  // Counter to force refetch when incremented
  const [refetchCounter, setRefetchCounter] = useState(0);

  // Track previous lessonId so we can detect leaving a lesson route.
  const prevLessonIdRef = useRef<string | undefined>(lessonId);

  // Invalidate all lessons caches (week lessons + subjects) to trigger refetch
  const invalidateLessonsCache = useCallback(() => {
    // Invalidate week lessons cache
    lastFetchedWeekKeyRef.current = undefined;
    setRefetchCounter(c => c + 1);
    // Invalidate subjects cache (lessons by subject view)
    invalidateSubjectsCache();
  }, [invalidateSubjectsCache]);

  // ── Refetch when leaving a lesson route (BLD2-547) ────────────
  // When lessonId goes from a value to undefined the student has navigated
  // back from a lesson page to the week (or term) view.  Force a refetch so
  // the UI shows the authoritative status from the API — this covers the
  // cross-tab completion case where the StorageEvent-based sync may have
  // already fired but the GET response was served from a stale backend cache.
  useEffect(() => {
    const wasOnLesson = prevLessonIdRef.current !== undefined;
    const leftLesson = lessonId === undefined;
    prevLessonIdRef.current = lessonId;

    if (wasOnLesson && leftLesson) {
      invalidateLessonsCache();
    }
  }, [lessonId, invalidateLessonsCache]);

  // Fetch week lessons when URL params change or lessons cache is invalidated.
  // Uses URL-derived IDs directly so this fires in parallel with the
  // terms-summary request instead of waiting for it to resolve first.
  useEffect(() => {
    if (!urlGradeCode || !urlTermId || !urlWeekId) {
      setCurrentWeekSubjects([]);
      setWeekLessonsStatus(REQUEST_STATUS.IDLE);
      setWeekLessonsError(null);
      lastFetchedWeekKeyRef.current = undefined;
      return;
    }

    // Guard against malformed URL segments (e.g. /terms/term-abc/week-xyz)
    const termIdNum = Number(urlTermId);
    const weekIdNum = Number(urlWeekId);
    if (Number.isNaN(termIdNum) || Number.isNaN(weekIdNum)) return;

    const key = `${urlGradeCode}:${urlTermId}:${urlWeekId}`;

    // Avoid depending on request status here: setting PENDING inside this
    // effect would otherwise cause an immediate rerun and duplicate the
    // initial week-lessons request before the first response resolves.
    if (lastFetchedWeekKeyRef.current === key) {
      return;
    }

    lastFetchedWeekKeyRef.current = key;
    setWeekLessonsStatus(REQUEST_STATUS.PENDING);
    setWeekLessonsError(null);

    // Track whether this effect instance has been superseded so a late
    // response from a previous term/week doesn't overwrite newer data.
    let cancelled = false;

    termsApi.getWeekLessons(urlGradeCode, termIdNum, weekIdNum)
      .then(response => {
        if (cancelled) return;
        setCurrentWeekSubjects(response.subjects);
        setWeekLessonsStatus(REQUEST_STATUS.SUCCESS);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const err = error as { customAttributes?: { httpErrorStatus?: number }; message?: string };
        setWeekLessonsError({
          code: err?.customAttributes?.httpErrorStatus ?? 0,
          message: err?.message ?? "Failed to fetch week lessons"
        });
        setWeekLessonsStatus(REQUEST_STATUS.ERROR);
        lastFetchedWeekKeyRef.current = undefined;
      });

    return () => {
      cancelled = true;
      // If this effect was superseded before the response arrived,
      // clear the ref so the next effect run will retry the fetch
      // instead of skipping it via the dedup guard above.
      lastFetchedWeekKeyRef.current = undefined;
    };
  }, [urlGradeCode, urlTermId, urlWeekId, refetchCounter]);

  // Derive selectedSubject from URL + currentWeekSubjects (reference, not copy).
  // Note: We intentionally don't guard on weekLessonsStatus here. During a
  // refetch (PENDING), we still have valid data in currentWeekSubjects from
  // the optimistic update. Returning null during refetch would cause the
  // lesson UI to unmount and remount, triggering removeChild errors when
  // third-party libraries (Learnosity, MathJax, Plotly) have modified the DOM.
  const selectedSubject = useMemo((): Subject | null => {
    if (!lessonSlug) return null;
    return currentWeekSubjects.find(subject => subject.slug === lessonSlug) ?? null;
  }, [lessonSlug, currentWeekSubjects]);

  // Derive selectedLesson from URL + currentWeekSubjects (reference, not copy).
  // Same rationale as selectedSubject above — keep stable during refetches.
  const selectedLesson = useMemo((): Lesson | null => {
    if (!lessonId || !lessonSlug) return null;
    const subjectGroup = currentWeekSubjects.find(s => s.slug === lessonSlug);
    if (!subjectGroup) return null;
    return subjectGroup.lessons.find(l => String(l.id) === String(lessonId)) ?? null;
  }, [lessonId, lessonSlug, currentWeekSubjects]);

  // ── Shared lesson-status update ──────────────────────────────
  // Both startLesson and completeLesson follow the same flow:
  //   guard → optimistic update → API call → cache invalidation.
  // The only differences are the target status and the API endpoint.

  type LessonParams = {
    gradeId: number;
    termId: number;
    weekId: number;
    lessonId: number;
    subjectSlug: string;
  };

  const updateLessonStatus = useCallback(
    async <T>(
      params: LessonParams,
      targetStatus: LESSON_STATUS.STARTED | LESSON_STATUS.COMPLETED,
      apiCall: (p: LessonParams) => Promise<T>
    ): Promise<T | undefined> => {
      // Find current lesson status
      const subject = currentWeekSubjects.find(s => s.slug === params.subjectSlug);
      const lesson = subject?.lessons.find(l => l.id === params.lessonId);

      // Skip if lesson is already completed
      if (lesson?.status === LESSON_STATUS.COMPLETED) {
        return undefined;
      }

      // Optimistic update BEFORE API call for immediate UI feedback.
      // This ensures the UI reflects the new status instantly (e.g. the
      // "Continue" button appears when navigating back to Introduction).
      setCurrentWeekSubjects(prev =>
        prev.map(subject =>
          subject.slug === params.subjectSlug
            ? {
                ...subject,
                lessons: subject.lessons.map(lesson =>
                  lesson.id === params.lessonId
                    ? { ...lesson, status: targetStatus }
                    : lesson
                )
              }
            : subject
        )
      );

      const response = await apiCall(params);
      // Invalidate dependent caches so they refetch on next access.
      // We deliberately do NOT refetch week lessons here — the optimistic
      // update above already set the correct status locally.  Calling
      // invalidateLessonsCache() would trigger a re-fetch whose response
      // can race with the backend write and overwrite the optimistic state
      // with stale data, causing the CTA to revert ("Begin lesson" instead
      // of "Continue") and completed status to not register.  See: PLAN-313.
      invalidateTermsCache();
      invalidateSubjectsCache();

      // Signal other browser tabs that lesson data changed so they
      // refetch fresh data. See useCrossTabLessonSync hook.
      localStorage?.setItem?.("LESSON_STATUS_CHANGED", Date.now().toString());

      return response;
    },
    [currentWeekSubjects, invalidateTermsCache, invalidateSubjectsCache]
  );

  // Start lesson action
  const startLesson = useCallback(
    async (params: LessonParams) => {
      await updateLessonStatus(params, LESSON_STATUS.STARTED, lessonsApi.startLesson);
    },
    [updateLessonStatus]
  );

  // Complete lesson action.
  // Returns the csat_trigger payload when should_fire is true, otherwise null.
  // The caller (ReflectContainer) passes this as React Router navigation state
  // to SummaryContainer, which fires logEvent on mount. Keeping the fire point
  // in SummaryContainer guarantees Usersnap's URL filter sees /summary — no
  // timing workarounds needed.
  const completeLesson = useCallback(
    async (params: LessonParams): Promise<CsatTriggerPayload | null> => {
      const response = await updateLessonStatus(
        params,
        LESSON_STATUS.COMPLETED,
        lessonsApi.completeLesson<CompleteLessonResponse>
      );
      if (!response) return null;

      const csatTrigger = response.csat_trigger ?? null;
      return csatTrigger?.should_fire === true ? csatTrigger : null;
    },
    [updateLessonStatus]
  );

  // Fetch section content
  const fetchSectionContent = useCallback(
    <T,>(params: {
      section: string;
      gradeId: number;
      termId: number;
      weekId: number;
      lessonId: number;
      subjectSlug: string;
    }): Promise<T> => {
      return lessonsApi.getSectionContent<T>(params);
    },
    []
  );

  return {
    currentWeekSubjects,
    weekLessonsStatus,
    weekLessonsError,
    selectedSubject,
    selectedLesson,
    startLesson,
    completeLesson,
    fetchSectionContent,
    invalidateLessonsCache
  };
}
